import React, { useState, useEffect, useCallback } from 'react';
import { getActiveServices, createTransaction, getApiStatus, checkNumberCooldown } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { formatCurrency } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { ScrollArea } from '../../components/ui/scroll-area';
import {
  Smartphone,
  Loader2,
  CheckCircle,
  Wallet,
  Percent,
  AlertTriangle,
  ShieldAlert,
  Plus,
  X,
  Clock,
  XCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { ServiceGroupCard, groupServicesByCup } from '../../components/recharge/ServiceGroupCard';

export default function Recharge() {
  const { user, refreshUser } = useAuth();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedService, setSelectedService] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [phoneNumbers, setPhoneNumbers] = useState(['']);
  const [apiActive, setApiActive] = useState(true);
  const [processResults, setProcessResults] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(-1);
  const [countdown, setCountdown] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [servicesRes, apiStatusRes] = await Promise.all([
          getActiveServices(),
          getApiStatus(),
        ]);
        setServices(servicesRes.data);
        setApiActive(apiStatusRes.data.is_active);
      } catch (err) {
        toast.error('Error al cargar servicios');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const calculatePrice = (basePrice) => {
    const discount = user?.discount_percentage || 0;
    return basePrice - basePrice * (discount / 100);
  };

  const openConfirm = (service) => {
    setSelectedService(service);
    setPhoneNumbers(['']);
    setProcessResults([]);
    setProcessing(false);
    setCurrentIndex(-1);
    setCountdown(0);
    setConfirmOpen(true);
  };

  const addPhoneNumber = () => {
    setPhoneNumbers([...phoneNumbers, '']);
  };

  const removePhoneNumber = (index) => {
    if (phoneNumbers.length <= 1) return;
    setPhoneNumbers(phoneNumbers.filter((_, i) => i !== index));
  };

  const updatePhoneNumber = (index, value) => {
    const updated = [...phoneNumbers];
    updated[index] = value.replace(/\D/g, '');
    setPhoneNumbers(updated);
  };

  const validNumbers = phoneNumbers.filter((n) => n.length === 8);
  const totalPrice = calculatePrice(selectedService?.base_price || 0) * validNumbers.length;

  const getDuplicates = useCallback(() => {
    const counts = {};
    phoneNumbers.forEach((n) => {
      if (n.length === 8) counts[n] = (counts[n] || 0) + 1;
    });
    return counts;
  }, [phoneNumbers]);

  const hasDuplicates = Object.values(getDuplicates()).some((c) => c > 1);

  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

  const handleRecharge = async () => {
    if (validNumbers.length === 0) {
      toast.error('Agrega al menos un número válido de 8 dígitos');
      return;
    }

    const unitPrice = calculatePrice(selectedService.base_price);
    if (user.balance < unitPrice * validNumbers.length) {
      toast.error('Saldo insuficiente para todas las recargas');
      return;
    }

    setProcessing(true);
    setSubmitting(true);
    const results = validNumbers.map((n) => ({ number: n, status: 'pending', message: '' }));
    setProcessResults(results);

    const processed = {};

    for (let i = 0; i < validNumbers.length; i++) {
      setCurrentIndex(i);
      const num = validNumbers[i];
      const fullNum = `+53${num}`;

      if (processed[num]) {
        try {
          const cooldownRes = await checkNumberCooldown(fullNum);
          if (cooldownRes.data.cooldown) {
            let remaining = cooldownRes.data.remaining_seconds;
            results[i] = { ...results[i], status: 'waiting', message: `Esperando ${remaining}s...` };
            setProcessResults([...results]);

            while (remaining > 0) {
              setCountdown(remaining);
              results[i] = { ...results[i], message: `Esperando ${remaining}s para número repetido...` };
              setProcessResults([...results]);
              await sleep(1000);
              remaining--;
            }
            setCountdown(0);
          }
        } catch {
          // Continue anyway
        }
      }

      results[i] = { ...results[i], status: 'processing', message: 'Procesando...' };
      setProcessResults([...results]);

      try {
        await createTransaction({
          service_id: selectedService.id,
          phone_number: fullNum,
          operator: 'ETECSA Cuba',
        });
        results[i] = { ...results[i], status: 'success', message: 'Completado' };
        processed[num] = true;
      } catch (err) {
        const detail = err.response?.data?.detail || 'Error';
        results[i] = { ...results[i], status: 'error', message: detail };
      }
      setProcessResults([...results]);
    }

    setCurrentIndex(-1);
    setSubmitting(false);
    await refreshUser();

    const successCount = results.filter((r) => r.status === 'success').length;
    if (successCount === validNumbers.length) {
      toast.success(`${successCount} recarga(s) realizadas exitosamente`);
    } else if (successCount > 0) {
      toast.warning(`${successCount} de ${validNumbers.length} recargas completadas`);
    } else {
      toast.error('No se pudo completar ninguna recarga');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="recharge-page">
      <div>
        <h1 className="text-4xl font-black tracking-tight">Recargar ETECSA Cuba</h1>
        <p className="text-muted-foreground mt-1">
          Selecciona el monto para recargar a Cuba
        </p>
      </div>

      {/* User Balance Info */}
      <Card className="bg-gradient-to-r from-primary/10 to-accent/10 border-border/40">
        <CardContent className="p-4 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-lg bg-accent/20">
              <Wallet className="w-6 h-6 text-accent" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Tu Saldo</p>
              <p className="text-2xl font-black text-accent">
                {formatCurrency(user?.balance || 0)}
              </p>
            </div>
          </div>
          {user?.discount_percentage !== 0 && (
            <div className="flex items-center gap-2">
              <Percent className="w-4 h-4 text-primary" />
              <span className="text-sm text-muted-foreground">
                {user?.discount_percentage > 0 ? 'Descuento' : 'Recargo'}: <span className={`font-bold ${user?.discount_percentage > 0 ? 'text-emerald-400' : 'text-orange-400'}`}>
                  {user?.discount_percentage > 0 ? user.discount_percentage : `+${Math.abs(user.discount_percentage)}`}%
                </span>
              </span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* API Disabled Warning */}
      {!apiActive && (
        <Card className="border-2 border-destructive/40 bg-destructive/5" data-testid="api-disabled-banner">
          <CardContent className="p-4 flex items-center gap-4">
            <ShieldAlert className="w-8 h-8 text-destructive shrink-0" />
            <div>
              <p className="font-bold text-destructive">Sistema de Recargas Desactivado</p>
              <p className="text-sm text-muted-foreground">
                El sistema de recargas ha sido desactivado temporalmente. Contacte a su administrador.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Services Grid */}
      {services.length === 0 ? (
        <Card className="bg-card border-border/40">
          <CardContent className="text-center py-12 text-muted-foreground">
            <Smartphone className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No hay servicios disponibles</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {groupServicesByCup(services).map((group) => (
            <ServiceGroupCard
              key={group[0].id}
              group={group}
              user={user}
              apiActive={apiActive}
              calculatePrice={calculatePrice}
              onRecharge={openConfirm}
            />
          ))}
        </div>
      )}

      {/* Confirm Dialog */}
      <Dialog open={confirmOpen} onOpenChange={(open) => { if (!processing) setConfirmOpen(open); }}>
        <DialogContent className="bg-card border-border sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Confirmar Recarga</DialogTitle>
            <DialogDescription>
              {selectedService?.name} - {formatCurrency(calculatePrice(selectedService?.base_price || 0))} c/u
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {/* Phone Numbers List */}
            {!processing && (
              <div className="space-y-2">
                <Label>Números de Teléfono (Cuba +53)</Label>
                <ScrollArea className={phoneNumbers.length > 4 ? 'h-48' : ''}>
                  <div className="space-y-2 pr-2">
                    {phoneNumbers.map((num, idx) => {
                      const duplicateCount = getDuplicates();
                      const isDuplicate = num.length === 8 && duplicateCount[num] > 1;
                      return (
                        <div key={idx} className="flex items-center gap-2">
                          <span className="text-sm font-mono text-muted-foreground shrink-0">+53</span>
                          <Input
                            type="tel"
                            placeholder="5XXXXXXX"
                            value={num}
                            onChange={(e) => updatePhoneNumber(idx, e.target.value)}
                            className={`bg-secondary/50 font-mono flex-1 ${isDuplicate ? 'border-orange-400' : ''}`}
                            maxLength={8}
                            data-testid={`phone-input-${idx}`}
                          />
                          {isDuplicate && (
                            <Clock className="w-4 h-4 text-orange-400 shrink-0" title="Número repetido: 5 min de espera" />
                          )}
                          {phoneNumbers.length > 1 && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"
                              onClick={() => removePhoneNumber(idx)}
                              data-testid={`remove-phone-${idx}`}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </ScrollArea>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="w-full mt-2"
                  onClick={addPhoneNumber}
                  data-testid="add-phone-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Agregar Número
                </Button>
                {hasDuplicates && (
                  <p className="text-xs text-orange-400 flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    Los números repetidos tendrán 5 min de espera entre recargas
                  </p>
                )}
              </div>
            )}

            {/* Processing Results */}
            {processing && (
              <div className="space-y-2">
                <Label>Progreso de Recargas</Label>
                <ScrollArea className={processResults.length > 5 ? 'h-52' : ''}>
                  <div className="space-y-2 pr-2">
                    {processResults.map((result, idx) => (
                      <div
                        key={idx}
                        className={`flex items-center justify-between p-2 rounded-lg text-sm ${
                          result.status === 'success'
                            ? 'bg-emerald-500/10 border border-emerald-500/30'
                            : result.status === 'error'
                            ? 'bg-destructive/10 border border-destructive/30'
                            : result.status === 'processing' || result.status === 'waiting'
                            ? 'bg-primary/10 border border-primary/30'
                            : 'bg-secondary/30 border border-border/30'
                        }`}
                        data-testid={`result-${idx}`}
                      >
                        <div className="flex items-center gap-2">
                          {result.status === 'success' && <CheckCircle className="w-4 h-4 text-emerald-400" />}
                          {result.status === 'error' && <XCircle className="w-4 h-4 text-destructive" />}
                          {result.status === 'processing' && <Loader2 className="w-4 h-4 text-primary animate-spin" />}
                          {result.status === 'waiting' && <Clock className="w-4 h-4 text-orange-400" />}
                          {result.status === 'pending' && <div className="w-4 h-4 rounded-full border-2 border-muted-foreground/30" />}
                          <span className="font-mono">+53{result.number}</span>
                        </div>
                        <span className={`text-xs ${
                          result.status === 'success' ? 'text-emerald-400' :
                          result.status === 'error' ? 'text-destructive' :
                          result.status === 'waiting' ? 'text-orange-400' :
                          'text-muted-foreground'
                        }`}>
                          {result.message}
                        </span>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </div>
            )}

            {/* Summary Card */}
            <Card className="bg-secondary/30 border-border/30">
              <CardContent className="p-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Servicio:</span>
                  <span className="text-right" data-testid="confirm-service-name">{selectedService?.name}<span className="block text-xs text-muted-foreground">{selectedService?.description}</span></span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Precio unitario:</span>
                  <span className="font-mono">{formatCurrency(calculatePrice(selectedService?.base_price || 0))}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Cantidad de números:</span>
                  <span className="font-bold">{validNumbers.length}</span>
                </div>
                <div className="flex justify-between font-bold pt-2 border-t border-border/50">
                  <span>Total:</span>
                  <span className="text-accent text-lg">{formatCurrency(totalPrice)}</span>
                </div>
              </CardContent>
            </Card>
          </div>
          <DialogFooter>
            {!processing ? (
              <>
                <Button variant="outline" onClick={() => setConfirmOpen(false)}>
                  Cancelar
                </Button>
                <Button
                  onClick={handleRecharge}
                  disabled={submitting || validNumbers.length === 0}
                  className="btn-glow"
                  data-testid="confirm-recharge-btn"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Confirmar {validNumbers.length > 1 ? `${validNumbers.length} Recargas` : 'Recarga'}
                </Button>
              </>
            ) : (
              <Button
                variant="outline"
                onClick={() => { setProcessing(false); setConfirmOpen(false); }}
                disabled={submitting}
              >
                {submitting ? 'Procesando...' : 'Cerrar'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
